import { useState, useEffect } from 'react';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import { es } from 'date-fns/locale';
import AdminLayout from '@/components/admin/AdminLayout';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import axios from 'axios';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const locales = {
  'es': es,
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

const BOOKING_STATUS_OPTIONS = [
  { value: 'planificando', label: 'Planificando', color: 'bg-blue-100 text-blue-800' },
  { value: 'confirmado_pendiente_pago', label: 'Confirmado (Pendiente Pago)', color: 'bg-orange-100 text-orange-800' },
  { value: 'confirmado_pagado', label: 'Confirmado y Pagado', color: 'bg-green-100 text-green-800' },
  { value: 'cerrado', label: 'Cerrado', color: 'bg-gray-100 text-gray-800' },
  { value: 'cancelado', label: 'Cancelado', color: 'bg-red-100 text-red-800' }
];

const eventStyleGetter = (event) => {
  const statusOption = BOOKING_STATUS_OPTIONS.find(opt => opt.value === event.status);
  let backgroundColor = '#3174ad';
  
  switch(event.status) {
    case 'planificando':
      backgroundColor = '#3b82f6'; // blue
      break;
    case 'confirmado_pendiente_pago':
      backgroundColor = '#f97316'; // orange
      break;
    case 'confirmado_pagado':
      backgroundColor = '#10b981'; // green
      break;
    case 'cerrado':
      backgroundColor = '#6b7280'; // gray
      break;
    case 'cancelado':
      backgroundColor = '#ef4444'; // red
      break;
  }

  return {
    style: {
      backgroundColor,
      borderRadius: '4px',
      opacity: 0.8,
      color: 'white',
      border: '0px',
      display: 'block'
    }
  };
};

export default function AdminCalendar() {
  const [bookings, setBookings] = useState([]);
  const [events, setEvents] = useState([]);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    try {
      const res = await axios.get(`${API}/bookings`, { headers });
      setBookings(res.data);
      
      // Convertir reservas a eventos del calendario
      const calendarEvents = res.data
        .filter(booking => statusFilter === 'all' || booking.status === statusFilter)
        .map(booking => {
          const startDate = new Date(booking.travel_date);
          const endDate = booking.end_date ? new Date(booking.end_date) : startDate;
          
          return {
            id: booking.id,
            title: `${booking.name} - ${booking.trip_title}`,
            start: startDate,
            end: endDate,
            status: booking.status,
            resource: booking
          };
        });
      
      setEvents(calendarEvents);
    } catch (err) {
      console.error('Error fetching bookings:', err);
      toast.error('Error al cargar las reservas');
    }
  };

  useEffect(() => {
    // Re-filtrar eventos cuando cambia el filtro de estado
    const calendarEvents = bookings
      .filter(booking => statusFilter === 'all' || booking.status === statusFilter)
      .map(booking => {
        const startDate = new Date(booking.travel_date);
        const endDate = booking.end_date ? new Date(booking.end_date) : startDate;
        
        return {
          id: booking.id,
          title: `${booking.name} - ${booking.trip_title}`,
          start: startDate,
          end: endDate,
          status: booking.status,
          resource: booking
        };
      });
    
    setEvents(calendarEvents);
  }, [statusFilter, bookings]);

  const handleSelectEvent = (event) => {
    setSelectedBooking(event.resource);
    setOpenDialog(true);
  };

  const handleStatusChange = async (newStatus) => {
    if (!selectedBooking) return;

    try {
      await axios.put(
        `${API}/bookings/${selectedBooking.id}/status?status=${newStatus}`,
        {},
        { headers }
      );
      
      // Actualizar localmente
      setBookings(bookings.map(b => 
        b.id === selectedBooking.id ? { ...b, status: newStatus } : b
      ));
      setSelectedBooking({ ...selectedBooking, status: newStatus });
      
      toast.success('Estado actualizado correctamente');
    } catch (err) {
      console.error('Error updating status:', err);
      toast.error('Error al actualizar el estado');
    }
  };

  const getStatusBadge = (status) => {
    const statusOption = BOOKING_STATUS_OPTIONS.find(opt => opt.value === status);
    if (!statusOption) return null;
    return (
      <Badge className={statusOption.color}>
        {statusOption.label}
      </Badge>
    );
  };

  const formatDate = (dateStr) => {
    try {
      return format(new Date(dateStr), "dd/MM/yyyy", { locale: es });
    } catch {
      return dateStr;
    }
  };

  return (
    <AdminLayout>
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-serif font-semibold text-primary">
              Calendario de Reservas
            </h1>
            <p className="text-muted-foreground">
              {bookings.length} reservas en total
            </p>
          </div>

          <div className="flex items-center gap-3">
            <label className="text-sm font-medium">Filtrar por estado:</label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-64">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los estados</SelectItem>
                {BOOKING_STATUS_OPTIONS.map(option => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Leyenda de colores */}
        <div className="flex flex-wrap gap-3 mb-6">
          {BOOKING_STATUS_OPTIONS.map(option => (
            <div key={option.value} className="flex items-center gap-2">
              <div className={`w-4 h-4 rounded ${option.color.includes('blue') ? 'bg-blue-500' : option.color.includes('orange') ? 'bg-orange-500' : option.color.includes('green') ? 'bg-green-500' : option.color.includes('gray') ? 'bg-gray-500' : 'bg-red-500'}`}></div>
              <span className="text-sm">{option.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Calendario */}
      <div className="bg-white rounded-xl shadow-sm p-6" style={{ height: '700px' }}>
        <Calendar
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          style={{ height: '100%' }}
          onSelectEvent={handleSelectEvent}
          eventPropGetter={eventStyleGetter}
          culture="es"
          messages={{
            next: "Siguiente",
            previous: "Anterior",
            today: "Hoy",
            month: "Mes",
            week: "Semana",
            day: "Día",
            agenda: "Agenda",
            date: "Fecha",
            time: "Hora",
            event: "Evento",
            noEventsInRange: "No hay reservas en este rango de fechas",
            showMore: (total) => `+ Ver ${total} más`
          }}
        />
      </div>

      {/* Diálogo de detalle de reserva */}
      <Dialog open={openDialog} onOpenChange={setOpenDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{selectedBooking?.trip_title}</DialogTitle>
            <DialogDescription>
              Reserva de {selectedBooking?.name}
            </DialogDescription>
          </DialogHeader>

          {selectedBooking && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Estado</p>
                  <div className="mt-1">{getStatusBadge(selectedBooking.status)}</div>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Cambiar estado</p>
                  <Select 
                    value={selectedBooking.status} 
                    onValueChange={handleStatusChange}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {BOOKING_STATUS_OPTIONS.map(option => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Cliente</p>
                  <p className="text-base">{selectedBooking.name}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Email</p>
                  <a href={`mailto:${selectedBooking.email}`} className="text-base text-primary hover:underline">
                    {selectedBooking.email}
                  </a>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Teléfono</p>
                  <a href={`tel:${selectedBooking.phone}`} className="text-base text-primary hover:underline">
                    {selectedBooking.phone}
                  </a>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Nº Viajeros</p>
                  <p className="text-base">{selectedBooking.num_travelers}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Fecha inicio</p>
                  <p className="text-base">{formatDate(selectedBooking.travel_date)}</p>
                </div>
                {selectedBooking.end_date && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Fecha fin</p>
                    <p className="text-base">{formatDate(selectedBooking.end_date)}</p>
                  </div>
                )}
              </div>

              {selectedBooking.price && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Precio</p>
                  <p className="text-base font-semibold">{selectedBooking.price} €</p>
                </div>
              )}

              {selectedBooking.budget && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Presupuesto del cliente</p>
                  <p className="text-base">{selectedBooking.budget}</p>
                </div>
              )}

              {selectedBooking.special_requests && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Notas / Solicitudes especiales</p>
                  <p className="text-base whitespace-pre-wrap bg-slate-50 p-3 rounded-lg">
                    {selectedBooking.special_requests}
                  </p>
                </div>
              )}

              {selectedBooking.source_message_id && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Origen</p>
                  <Badge variant="outline">Convertida desde mensaje</Badge>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
